"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    number: "01",
    icon: "MagnifyingGlassIcon",
    title: "Assessment",
    description:
      "We conduct a thorough analysis of your current operations, identifying inefficiencies, cost leaks, and untapped opportunities.",
  },
  {
    number: "02",
    icon: "LightBulbIcon",
    title: "Strategy",
    description:
      "We develop a tailored roadmap aligned with your business objectives, prioritizing high-impact improvements.",
  },
  {
    number: "03",
    icon: "WrenchScrewdriverIcon",
    title: "Implementation",
    description:
      "Our team executes the strategy with precision, integrating new systems and processes with minimal disruption.",
  },
  {
    number: "04",
    icon: "ArrowPathIcon",
    title: "Optimization",
    description:
      "We continuously monitor KPIs, refine processes, and adapt strategies to ensure sustained, measurable results.",
  },
];

export default function ApproachSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll(".reveal, .reveal-scale").forEach((i) => observer.observe(i));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-28 bg-[#A8A8A8]">
      <div className="absolute top-0 left-0 right-0 gold-line" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="reveal text-[11px] font-display uppercase tracking-[0.2em] text-gold mb-3">
            How We Work
          </p>
          <h2 className="reveal delay-100 text-4xl md:text-5xl font-display font-700 text-[#0C1B33] leading-tight tracking-tight">
            Our Approach
          </h2>
          <p className="reveal delay-200 text-[19px] font-body font-500 text-[#0C1B33]/80 mt-4 max-w-xl mx-auto leading-[1.75]">
            A disciplined, four-phase methodology that transforms operational challenges into competitive advantages.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`reveal delay-${(i + 1) * 100} relative group`}
            >
              {/* Connector line on desktop */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-full w-full h-px z-0"
                  style={{ background: "linear-gradient(to right, rgba(201,168,76,0.3), transparent)", width: "calc(100% - 2.5rem)", left: "calc(50% + 1.25rem)" }}
                />
              )}

              <div className="service-card-ash rounded-2xl p-7 h-full relative z-10">
                {/* Step number */}
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[11px] font-display font-700 uppercase tracking-[0.2em] text-gold/70">
                    {step.number}
                  </span>
                  <div className="w-9 h-9 rounded-lg bg-[rgba(184,134,11,0.18)] flex items-center justify-center group-hover:bg-[rgba(184,134,11,0.3)] transition-colors">
                    <Icon
                      name={step.icon as "MagnifyingGlassIcon"}
                      size={16}
                      className="text-gold"
                    />
                  </div>
                </div>

                <h3 className="text-[19px] font-display font-700 text-[#0C1B33] mb-3 tracking-tight group-hover:text-gold transition-colors">
                  {step.title}
                </h3>
                <p className="text-[16px] font-body font-500 text-[#0C1B33]/80 leading-[1.75]">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 gold-line" />
    </section>
  );
}